import {
    FetchResult,
    MutationFunctionOptions,
    useMutation,
} from '@apollo/client';
import { assert } from '@common/errors';
import {
    AnyAsset,
    getCurrentValue,
    PlentyGoalAccountFinancialEntity,
} from '@common/financialEntities';
import { RetirementGoal } from '@common/goals';
import {
    CurrentRateInfo,
    UpdateGoalDocument,
    UpdateGoalMutation,
    UpdateGoalMutationVariables,
} from '@common/graphql/generated';
import { AtomicStatus } from '@common/react/atomic';
import { useFinancialEntitiesContext } from '@common/react/financialEntities';
import { getGoalAccounts } from '@common/react/goals';
import { PartnerSwitcherType } from '@common/react/partnerships';
import { getPartnerName, useCurrentUser } from '@common/react/user';
import { IraType } from '@common/types';
import { sum } from '@common/util/arrays';
import { createContext, useContext, useState } from 'react';
import { configByGoalType } from '../config';
import { GoalConfig } from '../config/types';
import {
    getIncludedAccounts,
    getMyIncludableAccounts,
    useAssets,
} from './helpers';
import IncludedAccountsModal, {
    RETIREMENT_GOAL_INCLUDED_ACCOUNTS_MODAL_ID,
} from './IncludedAccountsModal';
import { OpenIraFlow } from './OpenIraFlow';
import RetirementGoalDetailView from './RetirementGoalDetailView';

type UpdateGoalFcn = (
    params: Omit<UpdateGoalMutationVariables, 'id'>,
    options?: MutationFunctionOptions<UpdateGoalMutation>
) => Promise<FetchResult<UpdateGoalMutation>>;

type RetirementGoalContext = {
    goal: RetirementGoal;
    goalConfig: GoalConfig<RetirementGoal>;
    updateGoal: UpdateGoalFcn;
    atomicStatus: AtomicStatus;
    isSharedGoal: boolean;
    currentRateInfo: CurrentRateInfo;
    partnerSwitcherType: PartnerSwitcherType;
    setPartnerSwitcherType: (partnerSwitcherType: PartnerSwitcherType) => void;
    possessiveStr: PossessiveString;
    includedAccounts: AnyAsset[];
    myIncludableAccounts: AnyAsset[];
    goalAccounts: PlentyGoalAccountFinancialEntity[];
    balance: number | undefined;
    setPosition: (position: GoalDetailPosition) => void;
    allowOpeningAccounts: boolean;
    myPlentyTraditionalIraStatus: AccountOnboardingStatus;
    myPlentyRothIraStatus: AccountOnboardingStatus;
};

type PossessiveString = 'Your' | 'Shared' | `${string}’s`;

export const RetirementGoalContext =
    createContext<RetirementGoalContext | null>(null);

export const useRetirementGoalContext = (): RetirementGoalContext => {
    const ctx = useContext(RetirementGoalContext);
    assert(ctx, 'Value must be passed to RetirementGoalContext');
    return ctx;
};

export interface GoalDetailViewContainerProps {
    id: string;
    onClose: VoidFunction;
}

type GoalDetailPosition = 'MAIN' | 'OPEN_TRADITIONAL_IRA' | 'OPEN_ROTH_IRA';

export type AccountOnboardingStatus =
    | 'NOT_STARTED'
    | 'IN_PROGRESS'
    | 'CONFIRMED';

const getMyIraOnboardingStatus = (
    goal: RetirementGoal,
    iraType: IraType
): AccountOnboardingStatus => {
    const goalAccountOnboarding = goal.goalAccountOnboardings.find(
        (o) => o.isMine && o.info.iraType === iraType
    );
    return !goalAccountOnboarding
        ? 'NOT_STARTED'
        : !goalAccountOnboarding.confirmedAt
          ? 'IN_PROGRESS'
          : 'CONFIRMED';
};

export const RetirementGoalDetailContainerInner = ({
    goal,
    currentRateInfo,
    atomicStatus,
    onClose,
}: {
    goal: RetirementGoal;
    currentRateInfo: CurrentRateInfo;
    atomicStatus: AtomicStatus;
    onClose: VoidFunction;
}) => {
    const isSharedGoal = goal.shareStatus === 'SHARED';

    const [position, setPosition] = useState<GoalDetailPosition>('MAIN');
    const [partnerSwitcherType, setPartnerSwitcherType] =
        useState<PartnerSwitcherType>('MINE');

    const { partnerInfo } = useCurrentUser();
    const assets = useAssets();
    const { fes } = useFinancialEntitiesContext();

    const [updateGoalMutation] = useMutation(UpdateGoalDocument);
    const goalConfig = configByGoalType[goal.type];

    const partnerName = getPartnerName(partnerInfo);

    const possessiveStr: PossessiveString =
        partnerSwitcherType === 'MINE'
            ? 'Your'
            : partnerSwitcherType === 'SHARED'
              ? 'Shared'
              : `${partnerName}’s`;

    const includedAccounts = getIncludedAccounts(
        assets,
        goal.id,
        partnerSwitcherType
    );

    const myIncludableAccounts = getMyIncludableAccounts(assets);

    const goalAccounts = getGoalAccounts(goal);

    const balance = includedAccounts.some((fe) => fe.latestSnapshot)
        ? sum(includedAccounts, getCurrentValue)
        : undefined;

    const updateGoal: UpdateGoalFcn = (params, options = {}) =>
        updateGoalMutation({
            ...options,
            variables: {
                ...params,
                id: goal.id,
            },
        });

    // Allow opening accounts (Traditional IRA, Roth IRA, brokerage) if there
    // are no other Retirement goals with goal account onboardings for this user
    const allowOpeningAccounts =
        fes.unfilteredData?.goals
            .filter((g) => g.type === 'RETIREMENT' && g.id !== goal.id)
            .flatMap((g) => g.goalAccountOnboardings.filter((o) => o.isMine))
            .length === 0;

    const myPlentyTraditionalIraStatus = getMyIraOnboardingStatus(
        goal,
        'TRADITIONAL'
    );
    const myPlentyRothIraStatus = getMyIraOnboardingStatus(goal, 'ROTH');

    return (
        <RetirementGoalContext.Provider
            value={{
                goal,
                goalConfig,
                updateGoal,
                atomicStatus,
                currentRateInfo,
                isSharedGoal,
                partnerSwitcherType,
                setPartnerSwitcherType,
                possessiveStr,
                includedAccounts,
                myIncludableAccounts,
                goalAccounts,
                balance,
                setPosition,
                allowOpeningAccounts,
                myPlentyTraditionalIraStatus,
                myPlentyRothIraStatus,
            }}
        >
            {position === 'MAIN' && (
                <RetirementGoalDetailView onClose={onClose} />
            )}
            {position === 'OPEN_TRADITIONAL_IRA' && (
                <OpenIraFlow iraType="TRADITIONAL" />
            )}
            {position === 'OPEN_ROTH_IRA' && <OpenIraFlow iraType="ROTH" />}
            {/**
             * This modal is included in the component tree so that it has
             * access to RetirementGoalContext.
             * Ref: https://github.com/plentyfinancial/plenty-app/pull/1073#discussion_r1367694106
             */}
            <IncludedAccountsModal
                // TODO: Try to bake this into the component, removing duplication
                id={RETIREMENT_GOAL_INCLUDED_ACCOUNTS_MODAL_ID}
            />
        </RetirementGoalContext.Provider>
    );
};
